import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { RefreshCw, Download, CheckCircle, AlertCircle, ArrowUpCircle, Clock, Info } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const UpdatesPanel = () => {
  const [checking, setChecking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const checkForUpdates = async () => {
    setChecking(true);
    setResult(null);
    try {
      const res = await axios.get(`${API_URL}/updates/check`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
      });
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not reach update server');
    } finally {
      setChecking(false);
    }
  };

  const applyUpdate = async () => {
    if (!result?.update_url) return;
    if (!window.confirm(
      `Apply update v${result.latest_version}?\n\nYour config, database and uploads will NOT be touched. The page will need a reload after.`
    )) return;

    setApplying(true);
    try {
      const res = await axios.post(
        `${API_URL}/updates/apply`,
        { download_url: result.update_url },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
      );
      toast.success(res.data.message || 'Update applied!');
      setResult(prev => ({ ...prev, has_update: false, local_version: prev.latest_version }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      {/* Header card */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">MorphBook Updates</h2>
            <p className="text-sm text-gray-500 mt-1">
              Keep your installation up to date with the latest features and fixes.
            </p>
          </div>
          <button
            onClick={checkForUpdates}
            disabled={checking}
            className="btn btn-primary flex items-center space-x-2"
          >
            <RefreshCw size={16} className={checking ? 'animate-spin' : ''} />
            <span>{checking ? 'Checking…' : 'Check for Updates'}</span>
          </button>
        </div>

        {/* Current version */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 border-t pt-4">
          <Info size={15} />
          <span>
            Installed version:{' '}
            <strong className="text-gray-900">
              {result?.local_version ?? '—'}
            </strong>
          </span>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className="card">
          {result.has_update ? (
            <>
              {/* Update available */}
              <div className="flex items-start space-x-3 mb-4">
                <ArrowUpCircle size={24} className="text-primary-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">
                    Update available — v{result.latest_version}
                  </p>
                  {result.release_date && (
                    <p className="text-sm text-gray-500 flex items-center space-x-1 mt-0.5">
                      <Clock size={13} />
                      <span>Released {result.release_date}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Changelog */}
              {result.changelog?.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">What's new</p>
                  {result.changelog.map((entry, i) => (
                    <div key={i}>
                      <p className="text-sm font-medium text-gray-800">v{entry.version}</p>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{entry.notes}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Safety notice */}
              <div className="flex items-start space-x-2 text-sm text-blue-700 bg-blue-50 rounded-lg p-3 mb-4">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>
                  Your <strong>config, database and uploaded files</strong> will not be affected.
                  The update only replaces application code.
                </span>
              </div>

              <button
                onClick={applyUpdate}
                disabled={applying || !result.update_url}
                className="btn btn-primary flex items-center space-x-2"
              >
                <Download size={16} />
                <span>{applying ? 'Applying update…' : `Apply Update v${result.latest_version}`}</span>
              </button>
            </>
          ) : (
            /* Up to date */
            <div className="flex items-center space-x-3">
              <CheckCircle size={24} className="text-green-500 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">You're up to date</p>
                <p className="text-sm text-gray-500">
                  v{result.local_version} is the latest version.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* How updates work */}
      <div className="card bg-gray-50 border border-gray-200">
        <h3 className="font-semibold text-gray-800 mb-3">How updates work</h3>
        <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
          <li>Click <strong>Check for Updates</strong> — your installation contacts morphbookpro.com</li>
          <li>If a newer version exists, a changelog and update button appear</li>
          <li>Click <strong>Apply Update</strong> — the new files are downloaded and installed automatically</li>
          <li>Reload the page to start using the updated version</li>
        </ol>
        <p className="text-xs text-gray-400 mt-3">
          Your database, configuration and uploaded images are never modified during an update.
        </p>
      </div>
    </div>
  );
};

export default UpdatesPanel;
